import { ChangeDetectorRef, DEFAULT_CURRENCY_CODE, effect, inject, Injectable, LOCALE_ID, OnDestroy, PipeTransform, untracked } from "@angular/core";
import { AppContext } from 'src/app/app/context';
import { AppSettingsDto } from "../../services/api.services";
import { LocalisationService } from "../../services/localisation.service";

// INFO: Overrides a pipe
// Reason behind is that LOCALE_ID cannot be changed dynamically (when we load settings, for example)
// We would either have to pass locale to every pipe in templates - but this override does that implicitly
@Injectable()
export abstract class SettingsDrivenPipeOverride<T> implements PipeTransform, OnDestroy {
  readonly appContext = inject(AppContext);
  readonly localisationService = inject(LocalisationService);
  readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly pipeLocale = inject(LOCALE_ID);
  readonly defaultCurrencyCode = inject(DEFAULT_CURRENCY_CODE);

  private _dirty = true;
  private _output: any;
  private _prevArgs: any[] = [];

  destroyed = false;

  constructor() {
    effect(() => {
      let _ = [this.appContext.locale(), this.appContext.timeZone()];
      if (!this._dirty) {
        this._dirty = true;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  transform(value: any, ...args: any[]) {
    if (!this._dirty) {
      if (!this.argsChange([value, ...args]))
        return this._output;
    }
    else {
      this._prevArgs = [value, ...args];
    }

    return untracked(() => {
      this._dirty = false;
      this._output = this.onTransform(this.getPipe(), this.appContext.settings(), value, ...args);
      return this._output;
    });
  }

  private argsChange(args: any[]): boolean {
    if (this._prevArgs.length !== args.length) {
      this._prevArgs = args;
      return true;
    }

    if (args.some((a, i) => a !== this._prevArgs[i])) {
      this._prevArgs = args;
      return true;
    }

    return false;
  }

  protected abstract getPipe(): T;
  protected abstract onTransform(pipe: T, settings: AppSettingsDto, value: any, ...args: any[]): any;
}
